import { episodeList, heroSlides } from "@/mocks/episodes";
import { castList } from "@/mocks/cast";
import { categoryMeta } from "@/mocks/site";
import type { CastMember, CategoryType, Episode } from "@/types";

export const episodes = episodeList as Episode[];
export const casts = castList as CastMember[];
export const slides = heroSlides;
export const categories = categoryMeta;

export function getEpisodeById(id: string): Episode | undefined {
  return episodes.find((episode) => episode.id === id);
}

export function getCastById(id: string): CastMember | undefined {
  return casts.find((member) => member.id === id);
}

export function getCastForEpisode(episode: Episode): CastMember[] {
  return episode.castIds
    .map((id) => getCastById(id))
    .filter((member): member is CastMember => Boolean(member));
}

export function getEpisodesByCast(castId: string): Episode[] {
  return episodes.filter((episode) => episode.castIds.includes(castId));
}

export function getRelatedEpisodes(episode: Episode, limit = 3): Episode[] {
  const scored = episodes
    .filter((item) => item.id !== episode.id)
    .map((item) => {
      let score = 0;
      item.tags.forEach((tag) => {
        if (episode.tags.includes(tag)) score += 2;
      });
      item.castIds.forEach((castId) => {
        if (episode.castIds.includes(castId)) score += 1;
      });
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.item);

  const result = [...scored];
  for (const item of episodes) {
    if (result.length >= limit) break;
    if (item.id !== episode.id && !result.some((picked) => picked.id === item.id)) {
      result.push(item);
    }
  }
  return result.slice(0, limit);
}

export interface SearchResult {
  id: string;
  kind: CategoryType;
  title: string;
  excerpt: string;
  episodeId: string;
  image: string;
  meta: string;
}

export function searchContent(keyword: string, kind: "all" | CategoryType = "all"): SearchResult[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  const results: SearchResult[] = [];

  if (kind === "all" || kind === "synopsis") {
    episodes.forEach((episode) => {
      const text = [episode.title, episode.summary, ...episode.synopsis, ...episode.tags]
        .join(" ")
        .toLowerCase();
      if (text.includes(query)) {
        results.push({
          id: `synopsis-${episode.id}`,
          kind: "synopsis",
          title: `第${episode.number}集 · ${episode.title}`,
          excerpt: episode.summary,
          episodeId: episode.id,
          image: episode.cover,
          meta: `剧情介绍 · ${episode.airDate}`,
        });
      }
    });
  }

  if (kind === "all" || kind === "updates") {
    episodes.forEach((episode) => {
      const text = [`第${episode.number}集`, episode.title, episode.airDate, episode.duration]
        .join(" ")
        .toLowerCase();
      if (text.includes(query)) {
        results.push({
          id: `updates-${episode.id}`,
          kind: "updates",
          title: `第${episode.number}集 · ${episode.title}`,
          excerpt: `${episode.airDate} 更新 · ${episode.duration} · ${episode.summary}`,
          episodeId: episode.id,
          image: episode.cover,
          meta: `剧集更新 · ${episode.airDate}`,
        });
      }
    });
  }

  if (kind === "all" || kind === "cast") {
    casts.forEach((member) => {
      const text = [member.name, member.role, member.bio, ...member.tags].join(" ").toLowerCase();
      if (text.includes(query)) {
        const firstEpisode = getEpisodesByCast(member.id)[0];
        results.push({
          id: `cast-${member.id}`,
          kind: "cast",
          title: `${member.name} · 饰 ${member.role}`,
          excerpt: member.bio,
          episodeId: firstEpisode ? firstEpisode.id : "",
          image: member.avatar,
          meta: "主演信息",
        });
      }
    });
  }

  return results;
}

export const hotTags = ["同学会", "旧账", "天台", "录音", "婚恋", "反转", "群像"];